import { useState } from "react"
import {
  App,
  Button,
  Empty,
  Flex,
  Input,
  Modal,
  Popconfirm,
  Spin,
  Typography,
} from "antd"
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type Modifier,
} from "@dnd-kit/core"
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { useStaffTemplateCategories } from "./useTemplateCategories"
import { SortableCategoryRow } from "./SortableCategoryRow"
import styles from "./index.css"

const VERTICAL_ONLY: Modifier = ({ transform }) => ({ ...transform, x: 0 })

export const CategoryManager = ({ onClose }: { onClose: () => void }) => {
  const {
    categories,
    loading,
    creating,
    deleting,
    dirty,
    savingOrder,
    orderFailed,
    orderError,
    error,
    create,
    remove,
    reorder,
    saveOrder,
    cancelOrder,
    reload,
  } = useStaffTemplateCategories()
  const { message } = App.useApp()
  const [name, setName] = useState("")
  const [dragging, setDragging] = useState(false)
  const [confirming, setConfirming] = useState("")
  const busy = creating || Boolean(deleting) || savingOrder
  const disabled = busy || loading || Boolean(error)
  const mutationDisabled = disabled || dirty || dragging || Boolean(confirming)
  const dragDisabled = disabled || Boolean(confirming) || orderFailed
  const closeDisabled = busy || dirty || dragging || Boolean(confirming)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  )

  const add = async () => {
    if (mutationDisabled || !name.trim()) return
    try {
      await create(name)
      setName("")
      void message.success("分类已添加")
    } catch (cause) {
      void message.error(
        cause instanceof Error ? cause.message : "分类添加失败",
      )
    }
  }
  const save = async () => {
    try {
      await saveOrder()
      void message.success("分类顺序已保存")
    } catch {
      // The hook keeps the draft and exposes an inline error for retry.
    }
  }
  const deleteCategory = async (category: string) => {
    try {
      await remove(category)
      void message.success("分类已删除")
    } catch (cause) {
      void message.error(
        cause instanceof Error ? cause.message : "分类删除失败",
      )
    } finally {
      setConfirming("")
    }
  }
  const finishDrag = ({ active, over }: DragEndEvent) => {
    setDragging(false)
    if (!dragDisabled && over) reorder(String(active.id), String(over.id))
  }

  return (
    <Modal
      title="管理分类"
      width={640}
      open
      onCancel={() => {
        if (!closeDisabled) onClose()
      }}
      closable={!closeDisabled}
      mask={{ closable: !closeDisabled }}
      keyboard={!closeDisabled}
      footer={
        <Flex
          align="center"
          justify="space-between"
          gap={8}
          wrap
        >
          <Typography.Text type="secondary">
            {dirty ? "分类顺序未保存" : ""}
          </Typography.Text>
          <Flex gap={8}>
            {dirty && (
              <>
                <Button
                  disabled={busy || dragging}
                  onClick={cancelOrder}
                >
                  取消排序
                </Button>
                <Button
                  type="primary"
                  disabled={loading || dragging}
                  loading={savingOrder}
                  onClick={() => void save()}
                >
                  保存顺序
                </Button>
              </>
            )}
            <Button
              disabled={closeDisabled}
              onClick={onClose}
            >
              关闭
            </Button>
          </Flex>
        </Flex>
      }
    >
      <Flex
        vertical
        gap={16}
      >
        <Flex gap={8}>
          <Input
            aria-label="新分类名称"
            placeholder="输入新分类名称"
            value={name}
            disabled={mutationDisabled}
            onChange={(event) => setName(event.target.value)}
            onPressEnter={() => void add()}
          />
          <Button
            icon={<PlusOutlined />}
            loading={creating}
            disabled={mutationDisabled || !name.trim()}
            onClick={() => void add()}
          >
            添加
          </Button>
        </Flex>
        <Typography.Text type="secondary">
          拖动左侧手柄调整分类顺序，点击“保存顺序”后生效。
        </Typography.Text>
        {error && (
          <Flex
            align="center"
            gap={8}
            role="alert"
          >
            <Typography.Text type="danger">{error}</Typography.Text>
            <Button
              size="small"
              disabled={busy || dirty}
              onClick={reload}
            >
              重试加载分类
            </Button>
          </Flex>
        )}
        {orderError && (
          <Typography.Text
            type="danger"
            role="alert"
          >
            {orderError}
          </Typography.Text>
        )}
        {loading ? (
          <Flex justify="center">
            <Spin />
          </Flex>
        ) : !categories.length && !error ? (
          <Empty
            description="暂无分类"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[VERTICAL_ONLY]}
            onDragStart={() => setDragging(true)}
            onDragEnd={finishDrag}
            onDragCancel={() => setDragging(false)}
          >
            <SortableContext
              items={categories}
              strategy={verticalListSortingStrategy}
            >
              <Flex
                vertical
                gap={12}
                className={styles.categoryList}
              >
                {categories.map((category) => (
                  <SortableCategoryRow
                    key={category}
                    category={category}
                    disabled={dragDisabled}
                  >
                    <Typography.Text className={styles.categoryLabel}>
                      {category}
                    </Typography.Text>
                    <Popconfirm
                      title="删除分类"
                      description={`确定删除分类“${category}”吗？`}
                      okText="删除"
                      cancelText="取消"
                      okButtonProps={{ danger: true }}
                      onOpenChange={(next) =>
                        setConfirming(next ? category : "")
                      }
                      onConfirm={() => deleteCategory(category)}
                    >
                      <Button
                        aria-label={`删除分类 ${category}`}
                        icon={<DeleteOutlined />}
                        danger
                        disabled={
                          disabled ||
                          dirty ||
                          dragging ||
                          Boolean(confirming && confirming !== category)
                        }
                        loading={deleting === category}
                      />
                    </Popconfirm>
                  </SortableCategoryRow>
                ))}
              </Flex>
            </SortableContext>
          </DndContext>
        )}
      </Flex>
    </Modal>
  )
}
